// Pure CSV/HTML parsing utilities for Sage imports

// Split a single CSV line into trimmed, unquoted cells (honours quoted commas).
function splitCsvLine(line) {
  const cols = []; let cur = '', inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  cols.push(cur.trim());
  return cols.map(c => c.replace(/^"|"$/g, '').trim());
}

// Locations export. Header-aware so it tolerates both the current Sage format
//   Location ID, Name, Parent, Manager
// and the older layout
//   Location ID, Location name, Parent ID, Parent - Name
// In the current format "Parent" is the parent's Location ID (a code that refers
// to another row), and there is no separate parent-name column — so we derive
// parentName from the parent code's own Name within the same file. Falls back to
// positional columns (0=code, 1=name, 2=parent) when no header alias matches.
export async function parseLocationsCSV(file) {
  const text = await file.text();
  const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const header = splitCsvLine(lines[0]).map(h => h.toLowerCase());
  const idxOf = (aliases, fallback) => {
    for (const a of aliases) { const i = header.indexOf(a); if (i !== -1) return i; }
    return fallback;
  };
  const iCode   = idxOf(['location id', 'locationid', 'location', 'id', 'code'], 0);
  const iName   = idxOf(['name', 'location name', 'displayname'], 1);
  const iParent = idxOf(['parent', 'parent id', 'parentid', 'parent id.'], 2);
  const iPName  = idxOf(['parent - name', 'parent name', 'parentname'], -1);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const sageCode    = cols[iCode];
    const displayName = cols[iName];
    if (!sageCode || !displayName) continue;
    rows.push({
      sageCode,
      displayName,
      parentId:   (iParent >= 0 ? cols[iParent] : '') || null,
      parentName: (iPName  >= 0 ? cols[iPName]  : '') || null,
    });
  }

  // No explicit parent-name column (current format): derive it from the parent
  // code's own Name so the Sage Locations list still shows a friendly parent.
  if (iPName === -1) {
    const nameByCode = new Map(rows.map(r => [r.sageCode, r.displayName]));
    for (const r of rows) {
      if (r.parentId && !r.parentName) r.parentName = nameByCode.get(r.parentId) || null;
    }
  }
  return rows;
}

// NOTE: there is deliberately no entity-list parser here. The Sage Entities
// import goes through the server pipeline (the 'Entities' import type in
// api/src/functions/sageProcessor.js, mapped by api/src/sageEntities.js), so a
// hand-uploaded list, an .xlsx workbook and an emailed export are all read by
// ONE parser — a browser-side copy would be a second one to keep in step, and
// could read neither of the two binary/markup formats Sage hands back.

// NOTE: there is deliberately no chart-of-accounts parser here either. The
// Sage "GL Accounts" report (Account number, Title, Normal balance) goes
// through the server pipeline (the 'ChartOfAccounts' import type in
// api/src/functions/sageProcessor.js, mapped by api/src/sageAccountRoster.js),
// so a hand upload, an .xlsx workbook and an emailed report are all read by ONE
// parser. The browser-side copy that used to live here read three formats none
// of which was the report, derived the account type on its own, and could not
// retire an account the report no longer carries.
